import { getQuery, parseURL } from 'ufo'
import type { QueryObject } from 'ufo'

type CopilotAdvisorName = {
  hashid: string | null
  first_name: string | null
  last_name: string | null
}

const slugify = (str: string | null): string | null => {
  if (!str) return null

  return str
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9 -]/g, '') // remove any non-alphanumeric characters
    .replace(/\s+/g, '-') // replace spaces with hyphens
    .replace(/-+/g, '-') // remove consecutive hyphens
}

export const getInsiderParam = ({ hashid, first_name, last_name }: CopilotAdvisorName): string => {
  return [hashid, slugify(first_name), slugify(last_name)].filter(Boolean).join('-')
}

export const getCopilotDomain = (domain: string): string => {
  // @TODO: Refactor and remove Tauck specific logic later
  return domain === 'tauck.com' ? 'www.tauck.com' : domain
}

const parseUrl = (url: string): URL | null => {
  try {
    return new URL(/^[a-z][a-z0-9+.-]*:\/\//i.test(url) ? url : `https://${url.replace(/^\/+/, '')}`)
  } catch {
    return null
  }
}

// Mirrors Partner::copilotPageUri in the API; keep the two in sync
export const getCopilotPageUrl = (url: string, domains: string[]): string | null => {
  const uri = parseUrl(url.trim())

  if (!uri || !['http:', 'https:'].includes(uri.protocol)) return null

  const path = uri.pathname.replace(/^\/+|\/+$/g, '')

  const isOwned = domains.some((domain) => {
    const allowed = parseUrl(domain)
    const allowedPath = allowed?.pathname.replace(/^\/+|\/+$/g, '') ?? ''

    return !!allowed && uri.hostname === allowed.hostname
      && (allowedPath === '' || path === allowedPath || path.startsWith(`${allowedPath}/`))
  })

  return isOwned ? uri.toString() : null
}

/**
 * Splits a page's ?url=… from the page's own query. Brands paste links in without encoding them, so a
 * link's own query (?a=1&b=2) would otherwise be read as the page's. An unencoded link runs to the end,
 * taking its query and fragment with it; an encoded one ends at the next parameter.
 */
export const parseLinkQuery = (fullPath: string): { url: string | undefined, query: QueryObject } => {
  const { search, hash } = parseURL(fullPath)
  const match = search.match(/^\?(.*?&)?url=(.*)$/)

  if (!match) return { url: undefined, query: getQuery(fullPath) }

  const before = (match[1] ?? '').replace(/&$/, '')
  const value = match[2] ?? ''

  if (/%3A|%2F/i.test(value)) {
    const [encoded = '', ...after] = value.split('&')

    return { url: safeDecode(encoded), query: getQuery(`?${[before, ...after].filter(Boolean).join('&')}`) }
  }

  return { url: value + hash, query: getQuery(`?${before}`) }
}

const safeDecode = (value: string): string => {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}
